import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {ArrowLeft} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import PhoneInput, { type PaisOpcao, PAISES, validarNumero } from '@/components/PhoneInput'
import {
  CONDICOES_SAUDE,
  PERGUNTAS,
  OPCOES_EXPERIENCIA,
  OPCOES_PRONTIDAO,
  OPCOES_OBSTACULOS,
  OPCOES_INTERESSES,
  OLEOS_INFO,
} from '@/types/diagnostico'
import { calcularOleosIndicados } from '@/utils/oleosLogic'
import { filtrarPrioridadesVisiveis } from '@/utils/prioridades'
import { copiarTexto } from '@/utils/clipboard'

type Step = 'lead' | 'condicoes' | 'perguntas' | 'perfil' | 'resultado'

function Diagnostico() {
  const [step, setStep] = useState<Step>('lead')
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [currentPerfil, setCurrentPerfil] = useState(0)
  const [conviteVisivel, setConviteVisivel] = useState(true)
  const [leadId, setLeadId] = useState<string | null>(() => {
    // Recuperar leadId do sessionStorage ao iniciar
    return sessionStorage.getItem('diagnostico_leadId') || null
  })

  // Dados do formulário
  const [nome, setNome] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')
  const [idade, setIdade] = useState('')
  const [pais, setPais] = useState('Brasil')
  const [paisObj, setPaisObj] = useState<PaisOpcao>(PAISES[0])
  const [condicoes, setCondicoes] = useState<string[]>([])
  const [respostas, setRespostas] = useState<Record<string, boolean>>({})
  const [experiencia, setExperiencia] = useState('')
  const [prontidao, setProntidao] = useState('')
  const [obstaculo, setObstaculo] = useState('')
  const [interesses, setInteresses] = useState<string[]>([])
  const [prioridades, setPrioridades] = useState<string[]>([])
  const [avisoMaxPrioridades, setAvisoMaxPrioridades] = useState(false)
  const [oleosIndicados, setOleosIndicados] = useState<string[]>([])
  const [alertas, setAlertas] = useState<string[]>([])

  const totalSteps = 27
  const currentStepNumber =
    step === 'lead'
      ? 1
      : step === 'condicoes'
      ? 2
      : step === 'perguntas'
      ? 3 + currentQuestion
      : step === 'perfil'
      ? 23 + currentPerfil
      : 27

  const progress = (currentStepNumber / totalSteps) * 100

  // Animação de entrada
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  }

  // Salvar lead inicial
  const salvarLeadInicial = async () => {
    try {
      const now = new Date().toISOString()
      // Formatar WhatsApp com prefixo correto
      // Montar WhatsApp com código do país
      const digitos = whatsapp.replace(/\D/g, '')
      const whatsappFormatado = paisObj.codigo
        ? `${paisObj.codigo}${digitos}`
        : digitos
      
      console.log('🔵 [DIAGNÓSTICO] Iniciando salvamento do lead:', { nome, email, pais })
      
      const lead = await lumi.entities.leads.create({
        nome,
        whatsapp: whatsappFormatado,
        email,
        idade: idade ? Number(idade) : null,
        pais,
        data_hora: now,
        status: 'Novo lead',
        condicoes_saude: [],
        respostas: {},
        oleos_indicados: [],
        experiencia_oleos: '',
        prontidao: '',
        obstaculo: '',
        objetivo_saude: '',
        prioridades: [],
        interesses: [],
        clicou_whatsapp: 'Não',
        compartilhou: 'Não',
        creator: 'system',
        createdAt: now,
        updatedAt: now,
      })
      
      console.log('✅ [DIAGNÓSTICO] Lead criado com sucesso! ID:', lead._id)
      
      // Persistir leadId em sessionStorage
      sessionStorage.setItem('diagnostico_leadId', lead._id)
      setLeadId(lead._id)
      
      toast.success('Dados salvos!')
    } catch (error) {
      console.error('❌ [DIAGNÓSTICO] Erro ao salvar lead:', error)
      toast.error('Erro ao salvar dados. Por favor, tente novamente.')
      throw error // Re-lançar erro para prevenir avanço
    }
  }

  // Atualizar lead
  const atualizarLead = async (dados: Partial<any>) => {
    if (!leadId) {
      console.error('❌ [DIAGNÓSTICO] LeadId não encontrado ao tentar atualizar. Dados:', dados)
      toast.error('Erro: sessão perdida. Por favor, recomece o diagnóstico.')
      return
    }
    
    try {
      console.log('🔵 [DIAGNÓSTICO] Atualizando lead', leadId, 'com dados:', Object.keys(dados))
      
      await lumi.entities.leads.update(leadId, {
        ...dados,
        updatedAt: new Date().toISOString(),
      })
      
      console.log('✅ [DIAGNÓSTICO] Lead atualizado com sucesso!')
    } catch (error) {
      console.error('❌ [DIAGNÓSTICO] Erro ao atualizar lead:', error)
      toast.error('Erro ao salvar progresso. Por favor, continue — tentaremos salvar novamente.')
    }
  }

  // Handlers
  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Validar WhatsApp antes de qualquer salvamento
    const validacao = validarNumero(whatsapp, paisObj)
    if (!validacao.valido) {
      toast.error(validacao.mensagem)
      return
    }
    if (!idade || isNaN(Number(idade)) || Number(idade) <= 0) {
      toast.error('Por favor informe sua idade')
      return
    }
    try {
      await salvarLeadInicial()
      setStep('condicoes')
    } catch (error) {
      console.error('Não foi possível avançar devido a erro no salvamento')
    }
  }

  const handleCondicoesSubmit = async () => {
    await atualizarLead({ condicoes_saude: condicoes })
    setStep('perguntas')
  }

  const handlePerguntaResposta = async (resposta: boolean) => {
    const perguntaId = PERGUNTAS[currentQuestion].id
    const novasRespostas = { ...respostas, [perguntaId]: resposta }
    setRespostas(novasRespostas)

    // Delay de 400ms antes de avançar
    setTimeout(() => {
      if (currentQuestion < PERGUNTAS.length - 1) {
        setCurrentQuestion(currentQuestion + 1)
      } else {
        atualizarLead({ respostas: novasRespostas })
        setStep('perfil')
      }
    }, 400)
  }

  const handlePerfilNext = async () => {
    if (currentPerfil === 0 && !experiencia) {
      toast.error('Selecione uma opção')
      return
    }
    if (currentPerfil === 1 && !prontidao) {
      toast.error('Selecione uma opção')
      return
    }
    if (currentPerfil === 2 && !obstaculo) {
      toast.error('Selecione uma opção')
      return
    }
    if (currentPerfil === 3 && interesses.length === 0) {
      toast.error('Selecione pelo menos uma opção')
      return
    }
    if (currentPerfil === 4 && prioridades.length === 0) {
      toast.error('Selecione pelo menos 1 prioridade')
      return
    }

    if (currentPerfil < 4) {
      setCurrentPerfil(currentPerfil + 1)
    } else {
      // Calcular óleos
      const resultado = calcularOleosIndicados(respostas, condicoes)
      setOleosIndicados(resultado.oleos)
      setAlertas(resultado.alertas)

      // Salvar resultado final
      await atualizarLead({
        experiencia_oleos: experiencia,
        prontidao,
        obstaculo,
        interesses,
        objetivo_saude: '',
        prioridades,
        oleos_indicados: resultado.oleos,
      })

      setStep('resultado')
    }
  }

  const handleVoltar = () => {
    if (step === 'condicoes') setStep('lead')
    else if (step === 'perguntas' && currentQuestion > 0) setCurrentQuestion(currentQuestion - 1)
    else if (step === 'perguntas' && currentQuestion === 0) setStep('condicoes')
    else if (step === 'perfil' && currentPerfil > 0) setCurrentPerfil(currentPerfil - 1)
    else if (step === 'perfil' && currentPerfil === 0) {
      setCurrentQuestion(PERGUNTAS.length - 1)
      setStep('perguntas')
    }
  }

  const handleCompartilhar = async () => {
    // Registrar compartilhamento no banco
    if (leadId) {
      try {
        console.log('🔵 [DIAGNÓSTICO] Registrando compartilhamento para lead', leadId)
        await lumi.entities.leads.update(leadId, {
          compartilhou: 'Sim',
          updatedAt: new Date().toISOString(),
        })
        console.log('✅ [DIAGNÓSTICO] Compartilhamento registrado')
      } catch (error) {
        console.error('❌ [DIAGNÓSTICO] Erro ao registrar compartilhamento:', error)
      }
    } else {
      console.warn('⚠️ [DIAGNÓSTICO] LeadId não encontrado ao tentar registrar compartilhamento')
    }

    const url = window.location.origin
    const texto = `Fiz um diagnóstico gratuito de saúde e emoções e descobri quais óleos essenciais meu corpo e mente precisam! É incrível como cada resposta revelou algo sobre mim que eu nem percebia. Vale muito a pena fazer — leva só 5 minutos e o resultado é completamente personalizado. Acesse aqui: ${url}`

    // Verifica se é mobile e tem suporte a navigator.share
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Diagnóstico de Saúde e Emoções',
          text: texto,
        })
      } catch (error) {
        // Usuário cancelou o compartilhamento
        console.log('Compartilhamento cancelado')
      }
    } else {
      // Desktop: copiar para área de transferência
      try {
        await copiarTexto(texto)
        toast.success('Link copiado! Cole e compartilhe com quem você quiser. 💚', {
          duration: 4000,
        })
      } catch (error) {
        toast.error('Erro ao copiar link')
      }
    }
  }

  const gerarMensagemWhatsApp = () => {
    const labelsPerguntas: Record<string, string> = {
      'sono': 'Sono',
      'disposicao': 'Disposição',
      'foco': 'Foco',
      'memoria': 'Memória',
      'ansiedade': 'Ansiedade física',
      'peso': 'Peso',
      'dores': 'Dores recorrentes',
      'medicamentos': 'Medicamentos',
      'inflamacoes': 'Inflamações',
      'intestino': 'Intestino',
      'vergonha': 'Vergonha',
      'culpa': 'Culpa',
      'apatia': 'Apatia',
      'tristeza': 'Tristeza',
      'medo': 'Medo',
      'escassez': 'Escassez',
      'raiva': 'Raiva',
      'orgulho': 'Orgulho',
      'estagnacao': 'Estagnação',
      'desequilibrio': 'Desequilíbrio geral',
    }

    let mensagem = `Olá! Acabei de fazer o Diagnóstico de Saúde e Emoções e quero conhecer meu protocolo personalizado.\n\n`
    
    mensagem += `*Nome:* ${nome}\n`
    mensagem += `*WhatsApp:* ${whatsapp}\n`
    mensagem += `*E-mail:* ${email}\n\n`

    // Condições de saúde
    mensagem += `*Condições de saúde:*\n`
    if (condicoes.length > 0 && !condicoes.includes('Nenhuma')) {
      condicoes.forEach(c => mensagem += `- ${c}\n`)
    } else {
      mensagem += `- Nenhuma\n`
    }
    mensagem += `\n`

    // Sintomas relatados
    const sintomasSim = Object.entries(respostas)
      .filter(([_, valor]) => valor)
      .map(([key]) => labelsPerguntas[key])
      .filter(Boolean)
    
    mensagem += `*Sintomas relatados (Sim):*\n`
    if (sintomasSim.length > 0) {
      sintomasSim.forEach(s => mensagem += `- ${s}\n`)
    } else {
      mensagem += `- Nenhum\n`
    }
    mensagem += `\n`

    // Óleos indicados
    mensagem += `*Óleos indicados:*\n`
    oleosIndicados.forEach(o => mensagem += `- ${o}\n`)
    mensagem += `\n`
    
    // Perfil
    mensagem += `*Experiência com óleos:* ${experiencia}\n\n`
    mensagem += `*Prontidão:* ${prontidao}\n\n`
    mensagem += `*Maior obstáculo:* ${obstaculo}\n\n`
    
    mensagem += `*Interesses:*\n`
    interesses.forEach(i => mensagem += `- ${i}\n`)
    mensagem += `\n`
    
    if (prioridades.length > 0) {
      mensagem += `*Prioridades selecionadas:*\n`
      prioridades.forEach(p => mensagem += `- ${p}\n`)
    }

    return mensagem
  }

  const handleWhatsAppClick = async () => {
    // Registrar clique no WhatsApp
    if (leadId) {
      try {
        console.log('🔵 [DIAGNÓSTICO] Registrando clique WhatsApp para lead', leadId)
        await lumi.entities.leads.update(leadId, {
          clicou_whatsapp: 'Sim',
          updatedAt: new Date().toISOString(),
        })
        console.log('✅ [DIAGNÓSTICO] Clique WhatsApp registrado')
      } catch (error) {
        console.error('❌ [DIAGNÓSTICO] Erro ao registrar clique WhatsApp:', error)
      }
    } else {
      console.warn('⚠️ [DIAGNÓSTICO] LeadId não encontrado ao tentar registrar clique WhatsApp')
    }
    
    // Redirecionar para WhatsApp
    const mensagemCodificada = encodeURIComponent(gerarMensagemWhatsApp())
    const whatsappUrl = `https://wa.me/5581988671210?text=${mensagemCodificada}`
    window.open(whatsappUrl, '_blank')
  }

  return (
    <div className="min-h-screen bg-bege-fundo">
      {/* Barra de progresso */}
      {step !== 'resultado' && (
        <div className="fixed top-0 left-0 w-full bg-white shadow-sm z-50">
          <div className="h-2 bg-bege-medio">
            <motion.div
              className="h-full bg-verde-escuro"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="container mx-auto px-4 py-3 flex justify-between items-center">
            {step !== 'lead' && (
              <button
                onClick={handleVoltar}
                className="flex items-center gap-2 text-texto-suave hover:text-texto-escuro transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Voltar
              </button>
            )}
            <div className="ml-auto text-sm text-texto-suave">
              {Math.round(progress)}%
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo */}
      <div className="pt-20 pb-12 px-4">
        <div className="container mx-auto max-w-3xl">
          <AnimatePresence mode="wait">
            {step === 'lead' && (
              <motion.div
                key="lead"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <div className="text-center mb-8">
                  <p className="text-marrom-suave italic mb-4">Antes de começar</p>
                  <h1 className="text-4xl font-cormorant font-semibold text-texto-escuro mb-4">
                    Me conta um pouco sobre você
                  </h1>
                  <p className="text-texto-suave">
                    Seus dados são usados apenas para enviar seu protocolo personalizado.
                  </p>
                </div>

                <form onSubmit={handleLeadSubmit} className="space-y-6">
                  <div>
                    <label className="block text-texto-escuro mb-2">Nome completo</label>
                    <input
                      type="text"
                      value={nome}
                      onChange={e => setNome(e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-texto-escuro mb-2">WhatsApp</label>
                    <PhoneInput
                      value={whatsapp}
                      onChange={(numero, paisSel) => {
                        setWhatsapp(numero)
                        setPaisObj(paisSel)
                        setPais(paisSel.nomeSalvo)
                      }}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-texto-escuro mb-2">Sua idade</label>
                    <input
                      type="number"
                      value={idade}
                      onChange={e => setIdade(e.target.value)}
                      required
                      inputMode="numeric"
                      placeholder="Ex: 35"
                      className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-texto-escuro mb-2">E-mail</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={!validarNumero(whatsapp, paisObj).valido || !idade || Number(idade) <= 0}
                    className="w-full bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Continuar →
                  </button>

                  <p className="text-sm text-texto-suave text-center">
                    🔒 Seus dados são confidenciais e não serão compartilhados.
                  </p>
                </form>
              </motion.div>
            )}

            {step === 'condicoes' && (
              <motion.div
                key="condicoes"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <div className="text-center mb-8">
                  <h1 className="text-4xl font-cormorant font-semibold text-texto-escuro mb-4">
                    Você tem alguma condição de saúde?
                  </h1>
                  <p className="text-texto-suave">Pode marcar mais de uma.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {CONDICOES_SAUDE.map(condicao => (
                    <label
                      key={condicao}
                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        condicoes.includes(condicao)
                          ? 'border-verde-claro bg-verde-muito-claro'
                          : 'border-bege-medio hover:border-verde-claro'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={condicoes.includes(condicao)}
                        onChange={e => {
                          if (condicao === 'Nenhuma') {
                            setCondicoes(e.target.checked ? ['Nenhuma'] : [])
                          } else {
                            const filtered = condicoes.filter(c => c !== 'Nenhuma')
                            if (e.target.checked) {
                              setCondicoes([...filtered, condicao])
                            } else {
                              setCondicoes(filtered.filter(c => c !== condicao))
                            }
                          }
                        }}
                        className="w-5 h-5 text-verde-escuro"
                      />
                      <span className="text-texto-escuro">{condicao}</span>
                    </label>
                  ))}
                </div>

                <button
                  onClick={handleCondicoesSubmit}
                  className="w-full bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all"
                >
                  Continuar →
                </button>
              </motion.div>
            )}

            {step === 'perguntas' && (
              <motion.div
                key={`pergunta-${currentQuestion}`}
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <div className="text-center mb-12">
                  <h1 className="text-3xl md:text-4xl font-cormorant font-semibold text-texto-escuro mb-8">
                    {PERGUNTAS[currentQuestion].texto}
                  </h1>

                  <div className="flex gap-4 justify-center">
                    <button
                      onClick={() => handlePerguntaResposta(true)}
                      className="px-12 py-4 bg-white border-2 border-bege-medio text-texto-escuro rounded-full font-medium hover:bg-verde-escuro hover:text-white hover:border-verde-escuro transition-all text-lg"
                    >
                      Sim
                    </button>
                    <button
                      onClick={() => handlePerguntaResposta(false)}
                      className="px-12 py-4 bg-white border-2 border-bege-medio text-texto-escuro rounded-full font-medium hover:bg-verde-escuro hover:text-white hover:border-verde-escuro transition-all text-lg"
                    >
                      Não
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 'perfil' && (
              <motion.div
                key={`perfil-${currentPerfil}`}
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                {currentPerfil === 0 && (
                  <div>
                    <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-8 text-center">
                      Se você já experimentou óleos essenciais, como foi sua experiência?
                    </h1>
                    <div className="space-y-3">
                      {OPCOES_EXPERIENCIA.map(opcao => (
                        <label
                          key={opcao}
                          className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            experiencia === opcao
                              ? 'border-verde-claro bg-verde-muito-claro'
                              : 'border-bege-medio hover:border-verde-claro'
                          }`}
                        >
                          <input
                            type="radio"
                            name="experiencia"
                            checked={experiencia === opcao}
                            onChange={() => setExperiencia(opcao)}
                            className="mr-3"
                          />
                          {opcao}
                        </label>
                      ))}
                    </div>
                    <button
                      onClick={handlePerfilNext}
                      className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all"
                    >
                      Continuar →
                    </button>
                  </div>
                )}

                {currentPerfil === 1 && (
                  <div>
                    <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-8 text-center">
                      Sabendo que existe um protocolo personalizado para os seus desconfortos, como você se descreveria agora?
                    </h1>
                    <div className="space-y-3">
                      {OPCOES_PRONTIDAO.map(opcao => (
                        <label
                          key={opcao}
                          className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            prontidao === opcao
                              ? 'border-verde-claro bg-verde-muito-claro'
                              : 'border-bege-medio hover:border-verde-claro'
                          }`}
                        >
                          <input
                            type="radio"
                            name="prontidao"
                            checked={prontidao === opcao}
                            onChange={() => setProntidao(opcao)}
                            className="mr-3"
                          />
                          {opcao}
                        </label>
                      ))}
                    </div>
                    <button
                      onClick={handlePerfilNext}
                      className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all"
                    >
                      Continuar →
                    </button>
                  </div>
                )}

                {currentPerfil === 2 && (
                  <div>
                    <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-8 text-center">
                      Qual é o maior obstáculo que você enfrenta quando pensa em cuidar da sua saúde de forma mais ativa?
                    </h1>
                    <div className="space-y-3">
                      {OPCOES_OBSTACULOS.map(opcao => (
                        <label
                          key={opcao}
                          className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            obstaculo === opcao
                              ? 'border-verde-claro bg-verde-muito-claro'
                              : 'border-bege-medio hover:border-verde-claro'
                          }`}
                        >
                          <input
                            type="radio"
                            name="obstaculo"
                            checked={obstaculo === opcao}
                            onChange={() => setObstaculo(opcao)}
                            className="mr-3"
                          />
                          {opcao}
                        </label>
                      ))}
                    </div>
                    <button
                      onClick={handlePerfilNext}
                      className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all"
                    >
                      Continuar →
                    </button>
                  </div>
                )}

                {currentPerfil === 3 && (
                  <div>
                    <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-3 text-center">
                      Além de cuidar da sua saúde, você tem interesse em alguma dessas possibilidades?
                    </h1>
                    <p className="text-center text-sm italic mb-8" style={{ color: '#888888' }}>
                      Você pode selecionar mais de uma opção
                    </p>
                    <div className="space-y-3">
                      {OPCOES_INTERESSES.map(opcao => (
                        <label
                          key={opcao}
                          className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            interesses.includes(opcao)
                              ? 'border-verde-claro bg-verde-muito-claro'
                              : 'border-bege-medio hover:border-verde-claro'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={interesses.includes(opcao)}
                            onChange={e => {
                              if (e.target.checked) {
                                setInteresses([...interesses, opcao])
                              } else {
                                setInteresses(interesses.filter(i => i !== opcao))
                              }
                            }}
                            className="mr-3"
                          />
                          {opcao}
                        </label>
                      ))}
                    </div>
                    <button
                      onClick={handlePerfilNext}
                      className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all"
                    >
                      Continuar →
                    </button>
                  </div>
                )}

                {currentPerfil === 4 && (() => {
                  const gruposVisiveis = filtrarPrioridadesVisiveis(respostas, condicoes)
                  return (
                    <div>
                      <p className="text-marrom-suave italic mb-4 text-center">Última pergunta</p>
                      <h1 className="text-3xl font-cormorant font-semibold text-texto-escuro mb-4 text-center">
                        Para finalizar: o que você mais quer resolver ou melhorar na sua saúde hoje?
                      </h1>
                      <p className="text-texto-suave mb-2 text-center">
                        Selecione até 3 prioridades.
                      </p>
                      <p className="text-sm text-texto-suave mb-8 text-center">
                        {prioridades.length}/3 selecionada(s)
                      </p>

                      {avisoMaxPrioridades && (
                        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6 rounded">
                          <p className="text-orange-800 text-sm">
                            Você já selecionou 3 prioridades. Desmarque uma para selecionar outra.
                          </p>
                        </div>
                      )}

                      {gruposVisiveis.length === 0 ? (
                        <div className="bg-verde-muito-claro border-l-4 border-verde-escuro p-6 rounded mb-6">
                          <p className="text-texto-escuro">
                            Com base nas suas respostas, não identificamos prioridades específicas para destacar. Você pode prosseguir para ver seu resultado.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-8">
                          {gruposVisiveis.map(grupo => (
                            <div key={grupo.titulo}>
                              <h3 className="text-xl font-cormorant font-semibold text-verde-escuro mb-4">
                                {grupo.titulo}
                              </h3>
                              <div className="space-y-3">
                                {grupo.opcoes.map(opcao => {
                                  const selecionada = prioridades.includes(opcao.id)
                                  return (
                                    <label
                                      key={opcao.id}
                                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                                        selecionada
                                          ? 'border-verde-claro bg-verde-muito-claro'
                                          : 'border-bege-medio hover:border-verde-claro'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selecionada}
                                        onChange={e => {
                                          if (e.target.checked) {
                                            if (prioridades.length >= 3) {
                                              setAvisoMaxPrioridades(true)
                                              setTimeout(() => setAvisoMaxPrioridades(false), 3500)
                                              return
                                            }
                                            setPrioridades([...prioridades, opcao.id])
                                            setAvisoMaxPrioridades(false)
                                          } else {
                                            setPrioridades(prioridades.filter(p => p !== opcao.id))
                                            setAvisoMaxPrioridades(false)
                                          }
                                        }}
                                        className="w-5 h-5 text-verde-escuro"
                                      />
                                      <span className="text-texto-escuro">{opcao.label}</span>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        onClick={handlePerfilNext}
                        className="w-full mt-8 bg-verde-escuro text-white py-4 rounded-full font-medium hover:bg-opacity-90 transition-all"
                      >
                        Ver meu resultado →
                      </button>
                    </div>
                  )
                })()}
              </motion.div>
            )}

            {step === 'resultado' && (
              <motion.div
                key="resultado"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                transition={{ duration: 0.4 }}
              >
                <div className="bg-verde-escuro text-white px-6 py-12 rounded-2xl mb-8 text-center">
                  <h1 className="text-4xl font-cormorant font-semibold mb-4">
                    Isso é o que o seu corpo e suas emoções estão pedindo, {nome.split(' ')[0]} 🌿
                  </h1>
                  <p className="text-lg">
                    De acordo com tudo que você respondeu, {oleosIndicados.length} óleos essenciais podem trazer mudanças reais para sua vida:
                  </p>
                </div>

                {/* Alertas */}
                {alertas.map((alerta, index) => (
                  <div
                    key={index}
                    className="bg-orange-100 border-l-4 border-orange-500 p-4 mb-6 rounded"
                  >
                    <p className="text-orange-800">{alerta}</p>
                  </div>
                ))}

                {/* Grade de óleos */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {oleosIndicados.map(oleo => {
                    const info = OLEOS_INFO[oleo]
                    return (
                      <div
                        key={oleo}
                        className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
                      >
                        <div className="text-4xl mb-3">{info.emoji}</div>
                        <h3 className="font-semibold text-texto-escuro mb-2">{info.nome}</h3>
                        <p className="text-sm text-texto-suave">{info.beneficio}</p>
                      </div>
                    )
                  })}
                </div>

                {/* Bloco inspiracional */}
                <div className="bg-verde-muito-claro border-l-4 border-verde-escuro p-6 mb-6 rounded">
                  <h3 className="font-semibold text-texto-escuro mb-3 text-lg">💡 Você pode começar no seu ritmo</h3>
                  <p className="text-texto-suave text-sm leading-relaxed">
                    Cada óleo essencial é uma ferramenta poderosa e versátil — um único frasco pode atender inúmeros desconfortos do dia a dia. Há óleos relaxantes, que promovem um sono tranquilo e aliviam tensões, e óleos revigorantes, que trazem foco, otimismo e energia. Juntos, eles formam um suporte completo para o seu bem-estar físico e emocional — para aquele momento de acordar com tensão, a ansiedade do dia, o cansaço que não passa, o desânimo que aparece. Os óleos essenciais não substituem tratamentos médicos, mas podem ser uma ferramenta diária de cuidado, equilíbrio e transformação — para você e para quem você ama.
                  </p>
                </div>

                {/* Caixas informativas */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
                  <p className="text-blue-900 text-sm">
                    Onde consta "Mix", trata-se de uma combinação de óleos essenciais em um único frasco, formulada para atuar de forma mais completa e potente — cuidando do desconforto físico e emocional ao mesmo tempo. Cada Mix é desenvolvido com óleos que se complementam e potencializam seus efeitos.
                  </p>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-8 rounded">
                  <p className="text-yellow-900 text-sm">
                    Para casos mais específicos de saúde não apresentados neste diagnóstico, é necessário consultar cada situação de forma particular. Este diagnóstico existe para que você perceba que há soluções naturais para cada pessoa — e que o seu bem-estar pode ser cuidado de forma individualizada.
                  </p>
                </div>

                {/* CTA */}
                <div className="bg-verde-muito-claro p-8 rounded-2xl mb-8">
                  <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-3">
                    Pronta(o) para cuidar da sua saúde física e emocional de verdade?
                  </h2>
                  <p className="text-texto-suave mb-6">
                    Agora que você já sabe quais óleos essenciais o seu corpo e mente precisam, o próximo passo é entender como usá-los. Vou te guiar passo a passo: como aplicar, as dosagens certas, como comprar e como criar uma rotina que realmente funciona para você.
                  </p>
                  <button
                    onClick={handleWhatsAppClick}
                    className="block w-full bg-[#25D366] text-white py-4 rounded-full font-medium text-center hover:bg-opacity-90 transition-all mb-4"
                  >
                    Quero dar o primeiro passo →
                  </button>
                  <button
                    onClick={handleCompartilhar}
                    className="block w-full bg-white border-2 border-verde-claro text-verde-escuro py-4 rounded-full font-medium text-center hover:bg-verde-muito-claro transition-all"
                  >
                    🔗 Compartilhar o diagnóstico com amigas(os)
                  </button>
                </div>

                {/* Bloco de convite — capacitação */}
                <AnimatePresence>
                  {conviteVisivel &&
                    interesses.length > 0 &&
                    !(interesses.length === 1 && interesses[0] === 'Só quero focar no meu bem-estar por agora') && (
                      <motion.div
                        key="convite-capacitacao"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10, transition: { duration: 0.4 } }}
                        transition={{ duration: 0.5 }}
                        className="mt-12"
                      >
                        <div
                          className="w-full mx-auto mb-8"
                          style={{ height: '1px', backgroundColor: '#A8DFC4' }}
                        />
                        <div className="bg-white rounded-2xl p-8 text-center shadow-md">
                          <div className="text-4xl mb-4">✨</div>
                          <h2 className="text-2xl md:text-3xl font-cormorant font-semibold text-texto-escuro mb-4">
                            Uma pergunta antes de você ir...
                          </h2>
                          <p className="text-texto-suave leading-relaxed mb-8 max-w-xl mx-auto">
                            Quer participar de uma seleção e capacitação para descobrir como você pode trabalhar com os óleos essenciais e ter uma nova renda escalável?
                          </p>
                          <a
                            href="https://descubraseusoleos.com.br/capacitacao?indicado_por=monica"
                            className="block w-full text-white py-4 rounded-full font-medium text-center transition-all mb-3 hover:opacity-90"
                            style={{ backgroundColor: '#2D6A4F' }}
                          >
                            Sim, quero saber mais!
                          </a>
                          <button
                            onClick={() => setConviteVisivel(false)}
                            className="text-texto-suave text-sm underline hover:text-texto-escuro transition-colors"
                          >
                            Não por enquanto
                          </button>
                        </div>
                      </motion.div>
                    )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default Diagnostico
